<template>
  <div class="card-wrapper">
    <b-card v-for="singleFaq in faq" :key="singleFaq.id" class="mb-3">
      <div class="d-xs-flex justify-content-between align-items-center">
        <div class="d-lg-flex d-block flex-grow-1">
          <p class="card-text w-100 mb-lg-0 mb-2 mr-2">
            {{ singleFaq.question }}
          </p>
        </div>

        <div class="actions d-flex justify-content-end mt-xs-0 mt-4">
          <i @click="showInfoModal(singleFaq.id)" class="glyph-icon simple-icon-pencil datalist-icon mr-md-3 mr-2"></i>

          <i @click="handleDelete(singleFaq.id)" class="glyph-icon simple-icon-trash datalist-icon"></i>
        </div>
      </div>
    </b-card>

    <!-- Modals -->
    <b-modal id="editSingleFaqModal" ref="editSingleFaqModal" size="lg" :title="'Edit FAQ'">
      <b-row v-if="!isLoading">
        <b-colxx xxs="12">
          <b-card class="mb-4">
            <b-form @submit.prevent="onSubmit" class="av-tooltip">
              <b-form-group :label="`${$t('pages.faq.question')} (EN)`">
                <b-form-input
                  type="text"
                  v-model="$v.editForm.questionEn.$model"
                  :state="!$v.editForm.questionEn.$error"
                />
                <b-form-invalid-feedback v-if="!$v.editForm.questionEn.required">
                  {{ $t('formErrors.enterQuestion') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group :label="`${$t('pages.faq.question')} (RU)`">
                <b-form-input
                  type="text"
                  v-model="$v.editForm.questionRu.$model"
                  :state="!$v.editForm.questionRu.$error"
                />
                <b-form-invalid-feedback v-if="!$v.editForm.questionRu.required">
                  {{ $t('formErrors.enterQuestion') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group :label="`${$t('pages.faq.answer')} (EN)`">
                <b-form-textarea
                  spellcheck="false"
                  v-model="$v.editForm.answerEn.$model"
                  :state="!$v.editForm.answerEn.$error"
                  rows="3"
                ></b-form-textarea>

                <b-form-invalid-feedback v-if="!$v.editForm.answerEn.required">
                  {{ $t('formErrors.enterAnswer') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group :label="`${$t('pages.faq.answer')} (RU)`">
                <b-form-textarea
                  spellcheck="false"
                  v-model="$v.editForm.answerRu.$model"
                  :state="!$v.editForm.answerRu.$error"
                  rows="3"
                ></b-form-textarea>

                <b-form-invalid-feedback v-if="!$v.editForm.answerRu.required">
                  {{ $t('formErrors.enterAnswer') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group :label="$t('pages.faq.sort')">
                <b-form-input
                  type="number"
                  v-model="$v.editForm.sort.$model"
                  :state="!$v.editForm.sort.$error"
                  min="0"
                />
                <b-form-invalid-feedback v-if="!$v.editForm.sort.required">
                  {{ $t('formErrors.enterSort') }}
                </b-form-invalid-feedback>
                <b-form-invalid-feedback v-else-if="!$v.editForm.sort.numeric">
                  Sort value must be a numeric character
                </b-form-invalid-feedback>
              </b-form-group>

              <b-button type="submit" variant="primary" class="mt-2">
                {{ $t('buttons.save') }}
              </b-button>
            </b-form>
          </b-card>
        </b-colxx>
      </b-row>
      <div v-else class="d-flex justify-content-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>

      <template slot="modal-footer">
        <b-button variant="secondary" @click="hideModal('editSingleFaqModal')">
          {{ $t('buttons.cancel') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
const { required, numeric } = require('vuelidate/lib/validators')

export default {
  props: {
    faq: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      editForm: {
        questionEn: '',
        questionRu: '',
        answerEn: '',
        answerRu: '',
        sort: 0
      },
      currentFaq: null
    }
  },
  validations: {
    editForm: {
      questionEn: { required },
      questionRu: { required },
      answerEn: { required },
      answerRu: { required },
      sort: { required, numeric }
    }
  },
  mounted() {
    this.$root.$on('bv::modal::show', (_bvEvent, modalId) => {
      if (modalId === 'editSingleFaqModal') {
        this.clearForm()
      }
    })
  },
  methods: {
    addNotification(type, title, message) {
      this.$notify(type, title, message, { duration: 3000, permanent: false })
    },
    handleDelete(id) {
      this.$confirm({
        message: this.$t('confirm-dialog.question'),
        button: {
          no: this.$t('confirm-dialog.no'),
          yes: this.$t('confirm-dialog.yes')
        },
        callback: (confirm) => {
          if (confirm) {
            this.$store.dispatch('faq/deleteSingleFaq', {
              id,
              vm: this
            })
          }
        }
      })
    },
    showInfoModal(id) {
      this.$bvModal.show('editSingleFaqModal')
      this.$store.dispatch('faq/fetchSingleFaq', { id, vm: this })
      this.currentFaq = id
    },
    onSubmit() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const payload = {
          question_en: this.editForm.questionEn,
          question_ru: this.editForm.questionRu,
          answer_en: this.editForm.answerEn,
          answer_ru: this.editForm.answerRu,
          sort: this.editForm.sort
        }

        this.$store.dispatch('faq/updateSingleFaq', {
          payload,
          id: this.currentFaq,
          vm: this
        })
      }
    },
    clearForm() {
      this.editForm.questionEn = ''
      this.editForm.questionRu = ''
      this.editForm.answerEn = ''
      this.editForm.answerRu = ''
      this.editForm.sort = 0
      this.currentFaq = null
    },
    hideModal(refname) {
      this.$refs[refname].hide()
    }
  }
}
</script>
